import { Vue, Component } from 'vue-property-decorator'
import StatisticalAnalysisHeader from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-header/statistical-analysis-header.vue'
import {
    initialIncidentStatisticsDossierState,
    navigationItems,
    StatisticalAnalysisDossierIncidentsState,
    StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter,
    StatisticalAnalysisIncidentsDossierModelSearchInfo
} from '@/modules/statistical-analysis-module/views/incidents-dossier/incidents-dossier.model'
import StatisticalAnalysisSidebar from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.vue'
import StatisticalAnalysisViewDetail from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail/statistical-analysis-view-detail.vue'
import StatisticalAnalysisTemporalFilter from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.vue'
import { StatisticalAnalysisTemporalFilterOptions } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { StatisticalAnalysisIncidentTypes } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisHeaderNavigationItems } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-header/statistical-analysis-header.models'
import { incidentsStatisticsDossierService } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.service'
import { StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics, StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState } from '@/modules/statistical-analysis-module/services/incidents/incidents-dossier.dto'
import { Nullable } from '@/shared/types/app.types'
import StatisticAnalysisExternalViewIncidentsStatistics from '@/modules/statistical-analysis-module/components/statistical-analysis-external-views/statistical-analysis-external-views-incidents/statistical-analysis-external-views-incidents-statistics.vue'
import StatisticalAnalysisSkeletonIncidentDossierBase from '@/modules/statistical-analysis-module/components/statistical-analysis-skeletons/statistical-analysis-skeleton-incident-dossier/statistical-analysis-skeleton-incident-dossier-base/statistical-analysis-skeleton-incident-dossier-base.vue'
import { isNil } from 'lodash'
import StatisticalAnalysisSkeletonIncidentDossierWidgets from '@/modules/statistical-analysis-module/components/statistical-analysis-skeletons/statistical-analysis-skeleton-incident-dossier/statistical-analysis-skeleton-incident-dossier-widgets/statistical-analysis-skeleton-incident-dossier-widgets.vue'

@Component({
    components: {
        StatisticalAnalysisSidebar,
        StatisticalAnalysisHeader,
        StatisticalAnalysisViewDetail,
        StatisticalAnalysisTemporalFilter,
        StatisticAnalysisExternalViewIncidentsStatistics,
        StatisticalAnalysisSkeletonIncidentDossierBase,
        StatisticalAnalysisSkeletonIncidentDossierWidgets
    }
})
export default class IncidentsDossier extends Vue {
    state: StatisticalAnalysisDossierIncidentsState
    constructor() {
        super()
        this.state = initialIncidentStatisticsDossierState()
    }

    get isInitDossier(): boolean {
        return !isNil(this.state.promiseNavigationItems)
    }

    get incidentSelected(): string {
        return this.state.externalInfo.trackingGeneralSelected?.label as string
    }

    get max(): Nullable<number> {
        return this.state.generalStatistics.max
    }

    get min(): Nullable<number> {
        return this.state.generalStatistics.min
    }

    get mean(): Nullable<number> {
        return this.state.generalStatistics.mean
    }

    get median(): Nullable<number> {
        return this.state.generalStatistics.median
    }

    get variation(): Nullable<number> {
        return this.state.generalStatistics.variation
    }

    get isLoading(): boolean {
        return this.state.loading
    }

    created(): void {
        this.$eventHub.$on('statistical-loading', this._onStatisticalLoadingGridTable)
    }

    beforeDestroy() {
        this.$eventHub.$off('statistical-loading', this._onStatisticalLoadingGridTable)
    }

    getTranslatedLabel(item: StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter): string {
        return this.$tc(item.text)
    }

    onShowItemSelected(itemSelected: StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisIncidentTypes>): void {
        this.state = {
            ...this.state,
            externalInfo: {
                ...this.state.externalInfo,
                trackingGeneralSelected: itemSelected
            }
        }
        this.state.promiseStatistics = this._getGeneralViewStatistics()
    }

    onTemporalFilter(value: StatisticalAnalysisTemporalFilterOptions): void {
        this.state.externalInfo.temporalFilter = value
        this.state.promiseStatistics = this._getGeneralViewStatistics()
    }

    onChangeTypeTimeFilter(value: StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter): void {
        this.state.promiseStatistics = this._getGeneralViewStatistics()
    }

    async onSearch(search: StatisticalAnalysisIncidentsDossierModelSearchInfo): Promise<void> {
        this.state = {
            ...this.state,
            externalInfo: {
                ...this.state.externalInfo,
                startDate: search.startDate,
                endDate: search.endDate,
                typeSearch: search.typeSearch,
                laboratories: search.laboratories,
                trackingGeneralSelected: null
            },
            headerData: {
                ...this.state.headerData,
                header: {
                    ...this.state.headerData.header,
                    startDate: search.startDate as Date,
                    endDate: search.endDate as Date
                },
                navigationItems: []
            }
        }
        this.state.promiseNavigationItems = this._getGeneralView()
    }

    private async _getGeneralView(): Promise<void> {
        const response = await this.$_toCallSecure<StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState[]>(incidentsStatisticsDossierService.getGeneralView(this.state.externalInfo))
        this.state.headerData.navigationItems = navigationItems().map((item: StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisIncidentTypes>) => {
            const foundKey: StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState = (response as StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState[]).find((state: StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState) => state.state === item.key) as StatisticalAnalysisIncidentStatisticsDossierCaseVirtualState
            item.value = foundKey.total
            return item
        })
    }

    private async _getGeneralViewStatistics(): Promise<void> {
        const response = await this.$_toCallSecure<StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics>(incidentsStatisticsDossierService.getGeneralViewStatistics(this.state.externalInfo))
        const data: StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics = response as StatisticalAnalysisIncidentsStatisticsDossierGeneralStatistics
        this.state.generalStatistics.max = data.max
        this.state.generalStatistics.min = data.min
        this.state.generalStatistics.mean = data.mean
        this.state.generalStatistics.median = data.median
        this.state.generalStatistics.variation = data.variation
    }

    private _onStatisticalLoadingGridTable(value: boolean): void {
        this.state.loading = value
    }
}
